import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import { type FormEvent, useState } from "react"
import { AuthLayout } from "@/components/auth-layout"
import { ErrorText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Field } from "@/components/ui/field"
import { authClient } from "@/lib/auth-client"
import { isOAuthPage } from "@/lib/oauth"
import { refreshSession } from "@/lib/queries"
import { invitationPreviewQuery } from "@/lib/settings-api"
import { signUpRoute } from "@/router"

/** Decision 0130: the invitation a sign-up comes back to (`/invite/<id>[?k=<key>]`), or null. */
export const inviteOfRedirect = (
  redirect: string | undefined,
): { readonly invitationId: string; readonly key?: string } | null => {
  const m = redirect ? /^\/invite\/([A-Za-z0-9_-]{1,128})(?:\?k=([A-Za-z0-9_-]{22}))?$/.exec(redirect) : null
  if (!m?.[1]) return null
  return m[2] ? { invitationId: m[1], key: m[2] } : { invitationId: m[1] }
}

/**
 * Decision 0130 addendum: what a sign-up from an invite sends besides the account: the invitation and its link key,
 * only when the link had one (the emailed link). The server decides whether the account starts verified.
 */
export const signUpInviteFields = (
  invite: ReturnType<typeof inviteOfRedirect>,
): { readonly inviteId?: string; readonly inviteKey?: string } =>
  invite?.key ? { inviteId: invite.invitationId, inviteKey: invite.key } : {}

/** The server's minimum (Better Auth `minPasswordLength` in the cloud's auth config). */
export const PASSWORD_MIN = 10

/** Polish 2026-10-23 §11: the address has an account already: the form offers sign-in instead. */
const EXISTS = "exists"

/** A sign-up error as the form shows it: `EXISTS` for an address with an account, else the server's message. */
export const signUpErrorOf = (error: {
  readonly code?: string | undefined
  readonly message?: string | undefined
}): string =>
  error.code?.startsWith("USER_ALREADY_EXISTS") || /^User already exists/.test(error.message ?? "")
    ? EXISTS
    : (error.message ?? "Sign up failed")

export function SignUpPage() {
  const navigate = useNavigate()
  const { redirect } = signUpRoute.useSearch()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  // decision 0130 (audit items 9, 10): from an invite, say where they are joining and use the invited address
  const invite = inviteOfRedirect(redirect)
  const preview = useQuery({
    ...invitationPreviewQuery(invite?.invitationId ?? "", invite?.key),
    enabled: invite !== null,
  }).data
  const pendingInvite = preview && preview.status === "pending" ? preview : null
  const lockedEmail = pendingInvite?.email ?? null

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setPending(true)
    setError(null)
    const { error } = await authClient.signUp.email({
      name: String(form.get("name")),
      email: lockedEmail ?? String(form.get("email")),
      password: String(form.get("password")),
      // decision 0130 addendum: from the emailed (keyed) link the server checks the key and starts the account verified
      ...signUpInviteFields(invite),
    } as Parameters<typeof authClient.signUp.email>[0])
    setPending(false)
    if (error) return setError(signUpErrorOf(error))
    await refreshSession(queryClient)
    await (redirect
      ? navigate({ href: redirect, reloadDocument: isOAuthPage(redirect) })
      : navigate({ to: "/" }))
  }

  return (
    <AuthLayout
      title="Create your account"
      description={
        pendingInvite
          ? `Create your account to join ${pendingInvite.workspace.name}.`
          : "Set goals, and let your agents do the rest."
      }
      footer={
        <>
          Already have an account?{" "}
          <Link to="/sign-in" search={redirect ? { redirect } : {}} className="font-medium link">
            Sign in
          </Link>
        </>
      }
    >
      <form className="grid gap-4" onSubmit={onSubmit}>
        <Field id="name" label="Name" autoComplete="name" required />
        {lockedEmail ? (
          <div className="grid gap-1">
            <Field
              id="email"
              label="Email"
              type="email"
              autoComplete="email"
              value={lockedEmail}
              readOnly
              aria-describedby="email-invite-note"
            />
            <span id="email-invite-note" className="text-xs text-muted">
              The invitation is for this address.
            </span>
          </div>
        ) : (
          <div className="grid gap-1">
            <Field id="email" label="Email" type="email" autoComplete="email" required />
            {pendingInvite && (
              <span className="text-xs text-muted" data-invite-address="">
                Use the address the invitation was sent to ({pendingInvite.emailMasked}).
              </span>
            )}
          </div>
        )}
        <div className="grid gap-1">
          <Field
            id="password"
            label="Password"
            type="password"
            autoComplete="new-password"
            minLength={PASSWORD_MIN}
            required
            aria-describedby="password-hint"
          />
          <span id="password-hint" className="text-xs text-muted">
            At least {PASSWORD_MIN} characters.
          </span>
        </div>
        {error === EXISTS ? (
          <ErrorText>
            There is already an account for this address.{" "}
            <Link to="/sign-in" search={redirect ? { redirect } : {}} className="font-medium link">
              Sign in instead →
            </Link>
          </ErrorText>
        ) : (
          error && <ErrorText>{error}</ErrorText>
        )}
        <Button type="submit" disabled={pending}>
          {pending ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </AuthLayout>
  )
}
